import React, { useState, useEffect } from 'react';
import { SPM7_EXTREMES, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Zap, Play, Pause, Flame, CloudRain, Sun } from 'lucide-react';

interface SPM7FrequenciesProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SPM7Frequencies: React.FC<SPM7FrequenciesProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm7'];
  const audioEngine = AudioEngine.getInstance();

  const [selectedExtremeIdx, setSelectedExtremeIdx] = useState<number>(1); // 50-yr heat event
  const [warmingLevel, setWarmingLevel] = useState<'pi' | '1.5' | '2.0' | '4.0'>('4.0');

  const extreme = SPM7_EXTREMES[selectedExtremeIdx];

  // Frequency multiplier calculation
  let multiplier = 1.0;
  if (warmingLevel === '1.5') multiplier = extreme.temp1_5;
  if (warmingLevel === '2.0') multiplier = extreme.temp2_0;
  if (warmingLevel === '4.0') multiplier = extreme.temp4_0;

  // Pulse interval (ms) based on acceleration multiplier
  const baseIntervalMs = 1200;
  const pulseIntervalMs = Math.max(30, Math.round(baseIntervalMs / multiplier));

  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        if (!audioEngine.getIsUnlocked()) return;

        // Pitch maps to multiplier severity
        const pitch = 350 + multiplier * 15;
        audioEngine.triggerExtremeTransient(pitch, 0, 0.5);
      }, pulseIntervalMs);
    }
    return () => clearInterval(timer);
  }, [isPlaying, pulseIntervalMs, multiplier]);

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Pulse' : `Play ${multiplier.toFixed(1)}x Speed Pulse`}</span>
        </button>
      </div>

      {/* Main Acceleration Player Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        {/* Extreme Type Selector */}
        <div className="space-y-2">
          <span className="text-xs font-mono text-slate-400 block font-semibold">Select Extreme Event Type:</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {SPM7_EXTREMES.map((item, idx) => {
              const isSelected = selectedExtremeIdx === idx;
              return (
                <button
                  key={item.type}
                  onClick={() => setSelectedExtremeIdx(idx)}
                  className={`p-3.5 rounded-2xl border text-left font-sans text-xs transition-all ${
                    isSelected
                      ? 'bg-slate-800 border-cyan-400 text-white glow-cyan shadow-md'
                      : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <span className="font-bold block text-slate-100">{item.name}</span>
                  <span className="text-[10px] font-mono text-slate-400 block mt-1">Pre-Industrial: 1.0x</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Global Warming Level Buttons */}
        <div className="space-y-2 pt-2">
          <span className="text-xs font-mono text-slate-400 block font-semibold">Select Global Warming Level (GWL):</span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              onClick={() => setWarmingLevel('pi')}
              className={`p-3 rounded-2xl border font-mono text-xs font-bold transition-all ${
                warmingLevel === 'pi' ? 'bg-emerald-950 text-emerald-300 border-emerald-500/60' : 'bg-slate-900/60 text-slate-400 border-slate-800'
              }`}
            >
              Pre-Industrial (1.0x)
            </button>
            <button
              onClick={() => setWarmingLevel('1.5')}
              className={`p-3 rounded-2xl border font-mono text-xs font-bold transition-all ${
                warmingLevel === '1.5' ? 'bg-cyan-950 text-cyan-300 border-cyan-500/60' : 'bg-slate-900/60 text-slate-400 border-slate-800'
              }`}
            >
              +1.5°C GWL ({extreme.temp1_5.toFixed(1)}x)
            </button>
            <button
              onClick={() => setWarmingLevel('2.0')}
              className={`p-3 rounded-2xl border font-mono text-xs font-bold transition-all ${
                warmingLevel === '2.0' ? 'bg-amber-950 text-amber-300 border-amber-500/60' : 'bg-slate-900/60 text-slate-400 border-slate-800'
              }`}
            >
              +2.0°C GWL ({extreme.temp2_0.toFixed(1)}x)
            </button>
            <button
              onClick={() => setWarmingLevel('4.0')}
              className={`p-3 rounded-2xl border font-mono text-xs font-bold transition-all ${
                warmingLevel === '4.0' ? 'bg-red-950 text-red-300 border-red-500/60 glow-red' : 'bg-slate-900/60 text-slate-400 border-slate-800'
              }`}
            >
              +4.0°C GWL ({extreme.temp4_0.toFixed(1)}x)
            </button>
          </div>
        </div>

        {/* Speed Multiplier Highlight Box */}
        <div className="p-6 rounded-3xl bg-slate-950 border border-slate-800 text-center space-y-2">
          <span className="text-xs font-mono text-slate-400 block uppercase tracking-wider">Frequency Acceleration Rate</span>
          <div className="text-4xl lg:text-5xl font-black font-mono bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
            {multiplier.toFixed(1)}x Acceleration
          </div>
          <p className="text-xs text-slate-400">
            Event triggers every <strong className="text-white">{pulseIntervalMs} ms</strong> compared to {baseIntervalMs} ms baseline.
          </p>
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
